/**
 * Any error, as a sentence a person may read (#3012) — the KIT's copy of the
 * rule, so `@solum/ui` can apply it without depending on `@solum/sdk`.
 *
 * The UI/UX review found the raw transport envelope on screen — on a record's
 * related sections, `solum sdk: 403 — {"code":"42501", … "hint":"Grant the
 * required privileges … GRANT SELECT ON <schema.table> …"}`. Step 1 gave the
 * apps `readableError` in `@solum/sdk/src/refusal.ts`; the kit cannot import
 * the SDK, so its own render boundaries (ErrorState, the workbench panels'
 * refused/error notes, the caught errors of kit actions) use this one. The
 * sentences are the SDK's, word for word; the transport error is recognised by
 * SHAPE (`name`, `status`, `payload`) instead of by class, which is what keeps
 * this package free of the SDK.
 *
 *   · a transport error → the door's own sentence (+ hint) when it wrote one a
 *     person may read, else a sentence for its status, else `fallback`;
 *   · a human sentence (an Error the app threw, or a string a host worded) →
 *     unchanged;
 *   · anything that reads like Postgres, PostgREST, a GRANT hint or JSON →
 *     `fallback`. The raw text belongs in the console log, never on screen.
 *
 * Reconciliation: once the SDK may depend on this package, `@solum/sdk`'s
 * `readableError` can delegate here and the two copies become one.
 */

/** The sentence a caller gets when it does not word its own. */
export const DEFAULT_ERROR_SENTENCE = 'That could not be completed. Try again.'

/** What a person reads for a status the server gave no sentence for. */
const STATUS_SENTENCE: Record<string, string> = {
  '401': 'Your session has ended. Sign in again.',
  '403': 'You don’t have access to this.',
  '404': 'That could not be found.',
  '409': 'That changed in the meantime. Reload and try again.',
}
const SERVER_SENTENCE = 'The server could not complete this. Try again in a moment.'

/**
 * Text written for a developer, not a person. Narrower than the SDK's
 * `DB_PROSE` on purpose: here it also screens sentences a HOST worded ("This
 * column is required."), so it matches Postgres's own phrasing — quoted or
 * dotted identifiers, constraint wording — and the machine markers around it,
 * never a bare word like "column" or "relation".
 */
const MACHINE_TEXT = new RegExp([
  String.raw`^solum sdk\b`,
  String.raw`^\s*[{[]`,
  String.raw`"code"\s*:`,
  String.raw`\bSQLSTATE\b`,
  String.raw`\bPGRST\d{3}\b`,
  String.raw`\bGRANT\s+[A-Z][A-Z, ]*\s+ON\b`,
  String.raw`permission denied for\b`,
  String.raw`\b(?:relation|column|schema|table|type|function|role) "?[\w.]+"? does not exist\b`,
  String.raw`\bfunction [a-z_][\w.]*\(`,
  String.raw`syntax error at\b`,
  String.raw`violates [\w -]*constraint`,
  String.raw`null value in column\b`,
  String.raw`duplicate key value\b`,
  String.raw`in the schema cache\b`,
  String.raw`row-level security`,
].join('|'), 'i')

/** True when `text` is for the log, not for a person. */
export function isMachineText(text: string): boolean {
  return MACHINE_TEXT.test(text)
}

interface TransportShape { status: number; payload: string }

/** The SDK's `SolumTransportError`, recognised by shape rather than by class. */
function asTransport(e: unknown): TransportShape | null {
  if (!(e instanceof Error) || e.name !== 'SolumTransportError') return null
  const { status, payload } = e as Error & Partial<TransportShape>
  return typeof status === 'number' && typeof payload === 'string' ? { status, payload } : null
}

function messageOf(e: unknown): string {
  if (typeof e === 'string') return e
  const m = (e as { message?: unknown } | null | undefined)?.message
  return typeof m === 'string' ? m : ''
}

/** The sentence the door wrote, out of a transport envelope — or null. */
function doorSentence(json: string | undefined): string | null {
  if (!json) return null
  try {
    const parsed = JSON.parse(json) as { message?: unknown; hint?: unknown }
    const message = parsed.message
    if (message === 'step_up_required')
      return 'Verify your session with your authenticator, then retry this action.'
    if (typeof message !== 'string' || !message.trim() || isMachineText(message)) return null
    const hint = typeof parsed.hint === 'string' && parsed.hint.trim() && !isMachineText(parsed.hint)
      ? ` ${parsed.hint.trim()}`
      : ''
    return `${message.trim()}${hint}`
  } catch { return null }
}

/**
 * @param e        whatever was thrown or handed in — an Error, a transport
 *                 error, a string a host worded, or nothing.
 * @param fallback the caller's own sentence for when there is nothing a person
 *                 should read.
 */
export function readableError(e: unknown, fallback: string = DEFAULT_ERROR_SENTENCE): string {
  const raw = messageOf(e)
  const structured = asTransport(e)
  const envelope = structured ? null : /^solum sdk:\s*(\d{3})\b/i.exec(raw.trim())
  if (structured || envelope) {
    const status = structured ? String(structured.status) : (envelope?.[1] ?? '')
    const json = structured
      ? /^\s*(\{[\s\S]*\})\s*$/.exec(structured.payload)?.[1]
      : /—\s*(\{[\s\S]*\})\s*$/.exec(raw)?.[1]
    const said = doorSentence(json)
    if (said) return said
    if (/^5\d\d$/.test(status)) return SERVER_SENTENCE
    return STATUS_SENTENCE[status] ?? fallback
  }
  const text = raw.trim()
  if (!text || isMachineText(text)) return fallback
  return text
}
